import { Anchor, ErrorApi, ErrorNotLoggedIn, Preloader } from 'src/components'
import { savedTokens } from 'src/auth'
import { useGetOrganization } from './queries'
import { diffInDays } from 'src/utils/date'

import Kvp from 'src/components/Shared/UIComponents/Kvp'
import Switch from 'src/components/Shared/UIComponents/SwitchWithLabel'
import { AccessOrganization } from './AccessOrganization'
import InputField from 'src/components/Shared/UIComponents/InputField'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import { toast } from 'sonner'

type Props = {
  organization: string
  username: string
}

function Info(props: Props) {
  const { status, data, error, refetch } = useGetOrganization({
    organization: props.organization,
    username: props.username,
  })

  if (status === 'loading' || status === 'idle') {
    return <Preloader />
  }

  if (status === 'error') {
    return <ErrorApi message={error} onClick={refetch} />
  }

  if (data.setupStep !== 'finished') {
    toast.warning(`You didn't finish the setup yet.`)
    return null // FIXME: how to handle this case?
  }

  const getACSURL = () => {
    let url = 'https://{region}.mobileconnectcloud.com/token/sso?org={org}'
    url = url.replace('{region}', data.application.chatregion)
    url = url.replace('{org}', data.organization)
    return url
  }

  const getOrganizationConnectArn = () => {
    if (data.organization_connect_arn !== '') {
      return data.organization_connect_arn
    }

    let url = 'arn:aws:connect:{region}:{accountId}:instance/{instanceId}'
    url = url.replace('{region}', data.application.chatregion)
    url = url.replace(
      '{accountId}',
      data.organization_connect_account_id !== undefined
        ? data.organization_connect_account_id
        : 'ACCOUNT_ID',
    )
    url = url.replace(
      '{instanceId}',
      data.organization_connect_instance_id !== undefined
        ? data.organization_connect_instance_id
        : 'INSTANCE_ID',
    )
    return url
  }

  return (
    <div className="grid grid-cols-2 gap-6 px-6 mt-3">
      <Kvp
        label="organization"
        type="text"
        value={data.organization}
        tooltip={
          <p>This organisation name is used by users to login into the app.</p>
        }
      />
      <Kvp label="region" type="text" value={data.application.chatregion} />

      <Switch
        label="enabled"
        value={data.application.enabled.toLowerCase() === 'true'}
        disabled
        tooltip={
          <p>
            To disable your organisation and thus cancel your subscription, sign
            in to the AWS Management console and go to "Manage Subscriptions".
            More info{' '}
            <Anchor
              href="https://docs.aws.amazon.com/marketplace/latest/buyerguide/cancel-subscription.html#cancel-saas-subscription"
              target="_blank"
            >
              here
            </Anchor>
            .
          </p>
        }
      />

      <Kvp
        label="billing"
        value={
          data.billing +
          (data.billing === 'trial' && data.createtimestamp
            ? ` (${10 - diffInDays(data.createtimestamp)} days)`
            : '')
        }
      />

      <Kvp label="ios" type="url" value={data.application.iosurl} />
      <Kvp label="android" type="url" value={data.application.androidurl} />

      <AccessOrganization
        organization_connect_arn={getOrganizationConnectArn()}
        aws_connect_saml_url={
          data.aws_connect_saml_url ? data.aws_connect_saml_url : ''
        }
      />

      {(data.aws_connect_type === 'SSO' ||
        data.aws_connect_type === 'SALESFORCE') && (
        <div className="flex items-end gap-4">
          <InputField
            label="ACS URL"
            placeholder=""
            value={getACSURL()}
            disabled
            setValue={() => {}}
            className="bg-foreground text-secondary"
          />
          <CustomButton
            type="button"
            label="copy"
            onClick={() => {
              navigator.clipboard.writeText(getACSURL())
              toast.success(`The ACS URL has been copied to your clipboard.`)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default function ValidateInfo() {
  const [tokens] = savedTokens.useState(null)

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return <Info organization={tokens.organization} username={tokens.username} />
}
